// File: /pages/Combinations.jsx
import React, { useEffect, useState } from 'react';
import AccordionFolder from '/components/Accordions/AccordionFolder';
import ContentLibrarySidebar from '/components/Layout/ContentLibrarySidebar';
import ContentDisplayTabs from '/components/Tabs/ContentDisplayTabs';
import VideoLinksDisplay from '/components/ContentDisplay/VideoLinksDisplay';
import PerspectiveManagerDisplay from '/components/ContentDisplay/PerspectiveManagerDisplay';
import ClassificationDisplay from '/components/ContentDisplay/ClassificationDisplay';
import AlternativeNamesDisplay from '/components/ContentDisplay/AlternativeNamesDisplay';
import PerspectiveCarousel from '/components/ContentDisplay/PerspectiveCarousel';
import DatabaseImage from '/components/Media/DatabaseImage';
import Link from 'next/link';
import infoPaneStyles from '/components/Panes/InfoPane.module.css';
import sectionStyles from '/components/Sections/Section.module.css';
import tabsStyles from '/components/Tabs/ContentDisplayTabs.module.css';
import displayStyles from '/components/ContentDisplay/ContentDisplay.module.css';
//import styles from './Combinations.module.css'; // Optional: additional CSS for this page

// Helper component to render a single Combination with a tabbed layout.
const CombinationContentDisplay = ({ combination }) => {
  const [activeTab, setActiveTab] = useState('Overview');

  // --- Overview Tab ---
  const overviewTabContent = (
    <div>
      <div className={tabsStyles.overviewContainer}>
        {/* Left Column: Metadata */}
        <div className={displayStyles.leftColumn}>
          <p>
            <strong>Status:</strong> {combination.status || 'N/A'}
          </p>
          <p>
            <strong>Submission Date:</strong>{' '}
            {combination.submission_date
              ? new Date(combination.submission_date).toLocaleDateString()
              : 'N/A'}
          </p>
          {combination.approval_date && (
            <p>
              <strong>Approval Date:</strong>{' '}
              {new Date(combination.approval_date).toLocaleDateString()}
            </p>
          )}
          <p>
            <strong>Created At:</strong>{' '}
            {combination.created_at
              ? new Date(combination.created_at).toLocaleDateString()
              : 'N/A'}
          </p>
          <p>
            <strong>Updated At:</strong>{' '}
            {combination.updated_at
              ? new Date(combination.updated_at).toLocaleDateString()
              : 'N/A'}
          </p>
        </div>
        {/* Right Column: Perspective Carousel and DMCA Link */}
        <div className={displayStyles.rightColumn}>
          <PerspectiveCarousel content={combination} />
          <div className={displayStyles.dmcaLinkContainer}>
            <Link
              href={`/DMCA?contentHash=${combination.content_id}`}
              className={displayStyles.dmcaLink}
            >
              DMCA Request
            </Link>
          </div>
        </div>
      </div>
      <p className={displayStyles.description}>
        <strong>Description:</strong> {combination.description || 'No description available'}
      </p>
      <AlternativeNamesDisplay contentId={combination.content_id} />
    </div>
  );

  // --- Classification Tab ---
  const classificationTabContent = (
    <ClassificationDisplay content={combination} />
  );

  // --- Media Tab ---
  const galleryImages = Array.isArray(combination.gallery_images)
    ? combination.gallery_images
    : [];
  const mediaTabContent = (
    <div>
      <h1>Gallery</h1>
      {galleryImages.length > 0 ? (
        <div className={tabsStyles.galleryContainer}>
          {galleryImages.map((image, index) => (
            <DatabaseImage
              key={index}
              image={image}
              alt={`Gallery Image ${index + 1}`}
              className={tabsStyles.galleryImage}
            />
          ))}
        </div>
      ) : (
        <p>No content available yet.</p>
      )}
      <h1>Perspectives</h1>
      <PerspectiveManagerDisplay content={combination} />
      <h1>Video Links</h1>
      <VideoLinksDisplay content={combination} />
    </div>
  );

  return (
    <ContentDisplayTabs
      tabs={[
        { label: 'Overview', content: overviewTabContent },
        { label: 'Media', content: mediaTabContent },
        { label: 'Classification', content: classificationTabContent },
      ]}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
    />
  );
};

const CombinationsPage = () => {
  const [combinations, setCombinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentCategoryMap, setParentCategoryMap] = useState({});

  // Fetch the parent category mapping from the API.
  useEffect(() => {
    const fetchParentCategories = async () => {
      try {
        const res = await fetch('/api/categories/categories');
        if (!res.ok) throw new Error('Failed to fetch parent categories');
        const data = await res.json();
        if (data.success && data.categories) {
          const map = {};
          data.categories.forEach((cat) => {
            // Use the category ID as key (as a string) and map to its name.
            map[String(cat.id)] = cat.category_name;
          });
          setParentCategoryMap(map);
        }
      } catch (err) {
        console.error('Error fetching parent categories mapping:', err);
      }
    };
    fetchParentCategories();
  }, []);

  // Fetch all content items from the API and filter for type "Combination".
  useEffect(() => {
    const fetchCombinations = async () => {
      try {
        const res = await fetch('/api/content/content');
        if (!res.ok) throw new Error('Failed to fetch combinations');
        const data = await res.json();
        let items = Array.isArray(data.content) ? data.content : [];
        // Filter for items where the "type" field equals "Combination" (case-insensitive).
        items = items.filter(
          (item) =>
            item.type && item.type.trim().toLowerCase() === 'combination'
        );
        setCombinations(items);
      } catch (err) {
        console.error('Error fetching combinations:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCombinations();
  }, []);

  if (loading) return <p>Loading combinations...</p>;
  if (error) return <p>Error: {error}</p>;

  // Group combinations by parent_category_id.
  const groups = {};
  combinations.forEach((combination) => {
    let parentCategories = [];
    if (combination.parent_category_id) {
      try {
        // Attempt to parse as JSON.
        parentCategories = JSON.parse(combination.parent_category_id);
        if (!Array.isArray(parentCategories)) {
          parentCategories = [parentCategories];
        }
      } catch (err) {
        // Fallback: treat it as a comma-delimited string.
        parentCategories = combination.parent_category_id.split(',').map((x) => x.trim());
      }
    }
    if (parentCategories.length === 0) {
      if (!groups['Uncategorized']) groups['Uncategorized'] = [];
      groups['Uncategorized'].push(combination);
    } else {
      parentCategories.forEach((cat) => {
        // Each category may be an object (with a "value" property) or a primitive.
        const key = String(cat.value || cat);
        if (!groups[key]) groups[key] = [];
        groups[key].push(combination);
      });
    }
  });

  // Convert the groups object into an array formatted for AccordionFolder.
  const groupsArray = Object.keys(groups).map((groupKey) => {
    let groupName = 'Category ' + groupKey;
    if (groupKey === 'Uncategorized') {
      groupName = 'Uncategorized';
    } else if (parentCategoryMap[groupKey]) {
      groupName = parentCategoryMap[groupKey];
    } else if (parentCategoryMap[parseInt(groupKey)]) {
      groupName = parentCategoryMap[parseInt(groupKey)];
    }
    return {
      groupHeader: groupName,
      items: groups[groupKey].map((combination) => ({
        header: combination.name,
        content: <CombinationContentDisplay combination={combination} />,
      })),
    };
  });

  return (
    <section id="combinations" className={`${sectionStyles.section} ${sectionStyles.sectionActive}`}>
      <div className={infoPaneStyles.infoPane}>
        <h1>Shuffle Dance Combinations - Master the Flow:</h1>
        <h2>
          Discover the combinations that bring shuffle dance moves together in fluid sequences. Click on an accordion header to view the combination details.
        </h2>
      </div>
      <div className={sectionStyles.pageContainer}>
        <div className={sectionStyles.mainContent}>
          {groupsArray.length > 0 ? (
            <AccordionFolder groups={groupsArray} />
          ) : (
            <p>No combinations found.</p>
          )}
        </div>
        <ContentLibrarySidebar contentType="Combination" />
      </div>
    </section>
  );
};

export default CombinationsPage;
